
  import { defineComponent, PropType } from 'vue';

  import { TFilter } from '@/common/components/redesigned/Filter/types/filter';
  import { toNumber, toNumberOrNull } from '@/common/utils/number';

  import EChartsColumnAndLine from '@/components/ChartComponents/apacheEcharts/EChartsColumnAndLine.vue';
  import EChartsVerticalBar from '@/components/ChartComponents/apacheEcharts/EChartsVerticalBar.vue';
  import type { TEchartsBarDatasetItem } from '@/components/ChartComponents/apacheEcharts/types';
  import chartConstants from '@/components/ChartComponents/chartConstants.js';
  import Loader from '@/components/Loader.vue';

  import { TChartBaseConfig, TChartData } from './chartTypes';

  type TValueFormatter = (value: number | string) => string;

  type TColumnAndLineItem = {
    name: string;
    data: Array<number | null>;
    color: string;
  };

  export default defineComponent({
    name: 'StatisticsVerticalColumnChart',
    components: {
      Loader,
      EChartsVerticalBar,
      EChartsColumnAndLine,
    },
    props: {
      configs: {
        type: Object as PropType<TChartBaseConfig>,
        required: true,
      },

      params: {
        type: Object as PropType<TFilter>,
        required: true,
      },

      columnColors: {
        type: Array as PropType<string[]>,
        default: () => ['#35B4B5', '#0078C8'],
      },

      lineColors: {
        type: Array as PropType<string[]>,
        default: () => ['#73EA48', '#989898', '#17A1FA', '#231F20'],
      },

      marker: {
        type: Object,
        default: () => ({
          size: 0,
          colors: [chartConstants.whiteColor],
          strokeColors: '#73EA48',
        }),
      },

      min: {
        type: Number,
      },

      sizeXYLabel: {
        type: Number,
        default: 15,
      },

      height: {
        type: Number,
        default: 490,
      },

      formatYLabelFunction: {
        type: Function,
        default: null,
      },

      formatLegendFunction: {
        type: Function,
        default: null,
      },

      formatTooltipFunction: {
        type: Function,
        default: null,
      },

      overlapColumns: {
        type: Boolean,
        default: false,
      },

      inverseOrderLegend: {
        type: Boolean,
        default: false,
      },

      inverseOrderTooltip: {
        type: Boolean,
        default: false,
      },
    },
    data(): {
      isLoading: boolean;
      abortController: AbortController | null;
    } {
      return {
        isLoading: false,
        abortController: null,
      };
    },
    created() {
      this.fetchData();
    },
    computed: {
      getHasLinesConfig(): boolean {
        return Array.isArray(this.configs.lines) && this.configs.lines.length > 0;
      },
      getEmptyMessage(): string {
        return this.isLoading ? '' : 'Данные отсутствуют';
      },
      getRawData() {
        return this.$store.getters[this.configs.storePaths.getter];
      },
      getData(): TChartData {
        const rawData = this.configs.getData ? this.configs.getData(this.getRawData) : this.getRawData;
        return Array.isArray(rawData) ? rawData : [];
      },
      getCategoryLabels(): string[] {
        const labelKeyStr = String(this.configs.labelKey);
        return this.getData.map((item) => String(item?.[labelKeyStr] ?? ''));
      },
      getTooltipLabels(): string[] {
        const tooltipKey = this.configs.tooltip?.key ?? this.configs.labelKey;
        const tooltipKeyStr = String(tooltipKey);
        return this.getData.map((item) => String(item?.[tooltipKeyStr] ?? ''));
      },
      getVerticalBarDataset(): TEchartsBarDatasetItem[] {
        const dataset = this.configs.columns.map((column, index) => {
          const rawName = typeof column.name === 'function' ? column.name(this.getRawData) : column.name;
          const secondColumn = this.configs.columns[1];
          const data =
            this.overlapColumns && index === 0 && secondColumn
              ? this.getData.map(
                  (item) => toNumber(item[String(column.valueKey)]) - toNumber(item[String(secondColumn.valueKey)]),
                )
              : this.getData.map((item) => toNumber(item[String(column.valueKey)]));

          return {
            label: rawName != null ? String(rawName) : undefined,
            data,
            color: this.columnColors[index] || this.columnColors[0],
          };
        });

        return this.overlapColumns ? [...dataset].reverse() : dataset;
      },
      getColumnAndLineColumnName(): string {
        const column = this.configs.columns[0];
        if (!column) return '';
        const rawName = typeof column.name === 'function' ? column.name(this.getRawData) : column.name;
        return rawName != null ? String(rawName) : '';
      },
      getColumnAndLineColumnData(): number[] {
        const column = this.configs.columns[0];
        if (!column) return [];
        return this.getData.map((item) => toNumber(item[String(column.valueKey)]));
      },
      getColumnAndLineLines(): TColumnAndLineItem[] {
        const linesCfg = this.configs.lines ?? [];

        return linesCfg.flatMap((line, index) => {
          const rawName = typeof line.name === 'function' ? line.name(this.getRawData) : line.name;
          const name = rawName != null ? String(rawName) : '';
          const data = this.getData.map((item) => toNumberOrNull(item[String(line.valueKey)]));
          const hasData = data.some((value) => value != null);

          if (!name || !hasData) return [];

          return [
            {
              name,
              data,
              color: this.lineColors[index] || this.lineColors[0],
            },
          ];
        });
      },
      getColumnAndLineColors(): string[] {
        return [this.columnColors[0] || '#35B4B5', ...this.getColumnAndLineLines.map((line) => line.color)];
      },
      getColumnAndLineMarker(): Record<string, unknown> {
        return {
          ...this.marker,
          size: this.marker.size || 4,
        };
      },
      getFormatLabelFunction(): TValueFormatter | undefined {
        const formatter = this.formatLegendFunction || this.formatYLabelFunction;
        if (!formatter) return undefined;
        return (value) => String(formatter(value));
      },
      getFormatValueFunction(): TValueFormatter | undefined {
        const formatter = this.formatTooltipFunction || this.formatYLabelFunction;
        if (!formatter) return undefined;
        return (value) => String(formatter(value));
      },
    },
    methods: {
      async fetchData() {
        this.isLoading = true;

        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        try {
          const data: TChartData = await this.searchData(this.params);
          this.setData(data);
        } catch (error) {
          console.error(error);
        } finally {
          this.isLoading = false;
        }
      },

      async searchData(params: TFilter) {
        return await this.$store.dispatch(this.configs.storePaths.action, {
          params,
          abortController: this.abortController,
        });
      },

      setData(data: TChartData) {
        this.$store.commit(this.configs.storePaths.mutation, { ...data });
      },
    },
    watch: {
      params: {
        handler() {
          this.fetchData();
        },
        deep: true,
      },
    },
  });
